import type { UnknownRecord } from "type-fest";
import { AssertionError } from "./assertion-error";

export type AssertIsDefinedContext = UnknownRecord & {
  id?: string;
};

/**
 * Checks if {@link value} exists.
 * @throws AssertionError If {@link value} is `null` or `undefined`
 * @param value The value to check.
 * @param id The id of the variable which is asserted
 * @param message A custom message for the error
 */
export function assertIsDefined<T>(
  value: T,
  id?: string,
  message?: string,
): asserts value is Exclude<T, null | undefined>;
/**
 * Checks if {@link value} exists.
 * @throws AssertionError If {@link value} is `null` or `undefined`
 * @param value The value to check.
 * @param context Any information to help identify where the error came from
 * @param message A custom message for the error
 */
export function assertIsDefined<T>(
  value: T,
  context?: AssertIsDefinedContext,
  message?: string,
): asserts value is Exclude<T, null | undefined>;
/**
 * Checks if {@link value} exists.
 * @throws AssertionError If {@link value} is `null` or `undefined`
 * @param value The value to check.
 * @param contextOrId Any information to help identify where the error came from
 * @param message A custom message for the error
 */
export function assertIsDefined<T>(
  value: T,
  contextOrId?: AssertIsDefinedContext | string,
  message?: string,
): asserts value is Exclude<T, null | undefined> {
  const context = (typeof contextOrId !== "string" ? contextOrId : {}) ?? {};

  if (context.id == null && typeof contextOrId === "string") {
    context.id = contextOrId;
  }

  context.id ??= "unknown";

  message ??= `Expected "${context.id}" to be defined but it was not.`;

  if (value == null) {
    throw new AssertionError(message, context);
  }
}
